import { World } from './world'
import { pickStageGoal } from '../data/maps'
import {
  cleanupDead,
  collideBreakables,
  collideEnemyProjectiles,
  collideProjectiles,
  collideProjectilesWithObstacles,
  fireWeapons,
  Intent,
  processExplosions,
  spawnFinalBoss,
  spawnPortal,
  updateEnemies,
  updateGimmick,
  updateFx,
  updatePickups,
  updatePlayer,
  updatePortal,
  updateProjectiles,
  updateShrines,
  updateSpawns,
  updateTelegraphs,
} from './systems'

/**
 * Advance the simulation by dt seconds. Only runs while the run is in the
 * 'playing' phase; the game layer handles level-up/pause/death/win screens and
 * is responsible for re-arming the phase afterwards.
 *
 * Uses a fixed-timestep accumulator for deterministic, stable physics.
 */
const FIXED = 1 / 60
const MAX_STEPS = 5

/** Advance the sim; returns how many tick()s actually ran (0 if not in the playing phase). The game layer uses
 *  the count to know whether an edge-triggered intent (jump/slide) was consumed this frame (see hit-stop latching). */
export function stepWorld(world: World, frameDt: number, intent: Intent): number {
  if (world.phase !== 'playing') return 0

  // reset per-frame feedback counters (audio/juice layer reads them after the step)
  world.shotsThisFrame = 0
  world.critsThisFrame = 0
  world.killsThisFrame = 0
  world.pickupsThisFrame = 0
  world.recoilThisFrame = false

  let acc = Math.min(frameDt, FIXED * MAX_STEPS)
  let ticks = 0
  while (acc >= FIXED) {
    tick(world, FIXED, intent)
    ticks++
    acc -= FIXED
    if (world.phase !== 'playing') return ticks
  }
  // run the remainder so motion stays smooth at high frame rates
  if (acc > 0) {
    tick(world, acc, intent)
    ticks++
  }
  return ticks
}

function tick(world: World, dt: number, intent: Intent): void {
  world.time += dt

  // kill-combo decay
  if (world.comboTimer > 0) {
    world.comboTimer -= dt
    if (world.comboTimer <= 0) world.combo = 0
  }
  // Critical Mass stacks decay if you stop critting
  if (world.critMassTimer > 0) {
    world.critMassTimer -= dt
    if (world.critMassTimer <= 0) world.critMassStacks = 0
  }

  updatePlayer(world, dt, intent)
  fireWeapons(world, dt)
  updateGimmick(world, dt)
  updateProjectiles(world, dt)
  updateEnemies(world, dt)
  collideProjectiles(world)
  collideProjectilesWithObstacles(world)
  collideBreakables(world)
  collideEnemyProjectiles(world)
  processExplosions(world)
  const { leveled } = updatePickups(world, dt)
  updateShrines(world, dt)
  updatePortal(world, dt)
  updateTelegraphs(world, dt)
  updateFx(world, dt)
  updateSpawns(world, dt)
  cleanupDead(world)

  // A clear on the SAME tick a lethal hit lands must beat the death screen: if the LAST stage's final boss is
  // already down, resolve the WIN before processing death (else a simultaneous boss-kill + player-death was
  // wrongly recorded as a loss — no map unlock, no rewards).
  if (world.objective === 'finalboss' && world.stage + 1 >= world.stages) {
    const fb = world.enemies.find((e) => e.id === world.finalBossId)
    if (!fb || !fb.alive) {
      world.objective = 'won'
      world.cleared = true
      world.phase = 'won'
      world.emit('won')
      return
    }
  }

  // death / revive
  if (world.player.hp <= 0 && world.player.alive) {
    const available = world.stats.revives - world.revivesUsed
    if (available > 0) {
      world.revivesUsed++
      world.player.hp = world.stats.maxHp * 0.5
      world.player.invuln = 2.5
      world.burst(world.player.x, world.player.z, 0xffd23d, 50, 2.5)
      world.floatText(world.player.x, world.player.z, 'REVIVE!', 0xffd23d, true)
      world.emit('revive')
    } else {
      world.player.alive = false
      world.phase = 'dead'
      world.emit('death')
      return
    }
  }

  // stage objective: complete the stage GOAL → reach & channel the Portal → Final Boss → Win
  if (world.objective === 'survive') {
    // EXPLORE (R1, Megabonk): the boss portal appears at the HALFWAY mark as a visible objective on the map — you can
    // rush it early (channel it now → boss) or keep farming the swarm for power. It only becomes the forced objective
    // once the stage goal completes; the channel works the whole time (updatePortal runs every tick).
    if (!world.portal && world.time >= world.portalAt * 0.5) spawnPortal(world)
    if (goalComplete(world)) {
      if (!world.portal) spawnPortal(world)
      world.objective = 'portal'
    }
  } else if (world.objective === 'portal') {
    // signpost the anti-stall auto-summon: a one-shot warning ~18s before, so it isn't a silent boss drop
    if (!world.portalWarned && world.portal && !world.portal.active && world.time >= world.map.surviveSeconds - 18) {
      world.portalWarned = true
      world.emit('portal-warning')
    }
    // anti-stall: if the player dawdles past the survive timer, the portal self-activates
    if (world.time >= world.map.surviveSeconds && world.portal && !world.portal.active) {
      world.portal.channel = 1
      world.portal.active = true
      world.finalSwarm = true
      spawnFinalBoss(world)
      world.objective = 'finalboss'
      world.emit('finalboss')
    }
  } else if (world.objective === 'finalboss') {
    const boss = world.enemies.find((e) => e.id === world.finalBossId)
    // (the LAST-stage clear → win is resolved above, before the death check, so it beats a same-tick lethal hit)
    if ((!boss || !boss.alive) && world.stage + 1 < world.stages) {
      // tier gauntlet: clear this stage and roll into the next, harder one
      world.stage++
      world.objective = 'survive'
      world.portal = null
      world.finalSwarm = false
      world.finalBossId = -1
      // chained stages are SUBSTANTIAL and escalate toward the finale (was a flat 25s sprint — Megabonk's later stages
      // are the meat/climax, not filler). Scales off the map's goalSeconds; the last stage is the longest. Deterministic,
      // no rng draw, and outside the canary's tier-1 single-stage path.
      world.portalAt = world.time + (world.map.goalSeconds ?? 180) * (0.4 + 0.15 * world.stage)
      world.difficulty *= 1.2
      world.curse += 1
      world.weaponsLocked = 0 // a dead boss's weapon-lock must not carry into the next stage
      world.telegraphs.length = 0 // nor its queued AoE telegraphs (they'd detonate on the player in the new stage)
      world.pendingExplosions.length = 0
      // the next stage picks its own goal (survive / elite-hunt …) — varies the gauntlet's pacing
      const g = pickStageGoal(world.map.id, world.tier, world.stage, world.rng)
      world.stageGoal = g.goal
      world.goalTarget = g.target
      world.goalProgress = 0
      world.emit('stage-clear')
    }
  }

  // level-up gate (pause for upgrade selection)
  if (leveled && world.pendingLevelUps > 0) {
    world.phase = 'levelup'
    world.emit('levelup')
  }
}

/** MP6 — has the stage's pre-portal goal been met (so the boss portal may appear)? */
function goalComplete(world: World): boolean {
  if (world.stageGoal === 'elite-hunt' || world.stageGoal === 'slay') return world.goalProgress >= world.goalTarget
  return world.time >= world.portalAt // 'survive' (default)
}
